<template>
  <div>
    <!-- Title -->
    <drop-down-menu-item :type="'title'">
      {{ sectionContent.title }}
    </drop-down-menu-item>

    <!-- Signed In Navigation -->
    <template v-if="isUserSignedIn">
      <!-- Inbox -->
      <drop-down-menu-item
        v-if="isMessageInboxEnabled && isOrderAheadFlow"
        :type="'routerLink'"
        :link-to="inboxNavigationLink"
        icon-class="far fa-inbox"
      >
        <span>
          {{ sectionContent.inbox }}

          <span
            v-if="unreadInboxMessageCount > 0"
            class="quarter-spacing-left cf-badge cf-badge--sm cf-badge--danger"
          >
            {{ unreadInboxMessageCount }}
          </span>
        </span>
      </drop-down-menu-item>

      <!-- Order Favorites -->
      <drop-down-menu-item
        v-if="isOrderFavoritesEnabled && isOrderAheadFlow"
        :type="'routerLink'"
        :link-to="orderFavoritesNavigationLink"
        :icon-class="'far fa-heart'"
      >
        {{ sectionContent.orderFavorites }}
      </drop-down-menu-item>

      <!-- My Account -->
      <drop-down-menu-item
        :type="'routerLink'"
        :link-to="accountNavigationLink"
        :icon-class="'far fa-user'"
      >
        {{ sectionContent.myAccount }}
      </drop-down-menu-item>

      <!-- Favorite Store -->
      <drop-down-menu-item
        v-if="isFavoriteStoreEnabled && isOrderAheadFlow"
        :type="'button'"
        :icon-class="'far fa-map-marker-alt'"
        @click="favoriteStoreClickHandler"
      >
        {{ sectionContent.favoriteStore }}

        <template
          v-if="currentUser.favoriteStore.description"
          #additional-content
        >
          <p class="cf-subtitle cf-subtitle--3 cf-subtitle--black">
            {{ currentUser.favoriteStore.description }}
          </p>
        </template>
      </drop-down-menu-item>

      <!-- Loyalty Rewards -->
      <drop-down-menu-item
        v-if="isOrdersLoyaltyEnabled && isOrderAheadFlow"
        :type="'routerLink'"
        :link-to="loyaltyNavigationLink"
        :icon-class="'far fa-gift'"
      >
        {{ sectionContent.loyaltyRewards }}
      </drop-down-menu-item>

      <!-- Loyalty Offers -->
      <drop-down-menu-item
        v-else-if="isLoyaltyOffersEnabled && isOrderAheadFlow"
        :type="'routerLink'"
        :link-to="offersNavigationLink"
        :icon-class="'far fa-gift'"
        :icon-inline-style="'vertical-align: text-bottom;'"
      >
        {{ sectionContent.loyaltyOffers }}
      </drop-down-menu-item>

      <!-- Payment Methods -->
      <drop-down-menu-item
        v-if="isPaymentManagementEnabled"
        :type="'routerLink'"
        :link-to="paymentMethodsNavigationLink"
        :icon-class="'far fa-credit-card'"
      >
        {{ sectionContent.paymentMethods }}
      </drop-down-menu-item>

      <!-- Order History -->
      <drop-down-menu-item
        v-if="isOrderHistoryEnabled"
        :type="'routerLink'"
        :link-to="orderHistoryNavigationLink"
        :icon-class="'far fa-history'"
      >
        {{ sectionContent.orderHistory }}
      </drop-down-menu-item>

      <!-- Sign Out -->
      <drop-down-menu-item
        :type="'button'"
        :link-class="'danger'"
        @click="signOutClickHandler"
      >
        {{ sectionContent.signOut }}
      </drop-down-menu-item>
    </template>

    <!-- Signed Out Navigation -->
    <template v-else>
      <!-- Sign In -->
      <drop-down-menu-item
        :type="'button'"
        :icon-class="'far fa-sign-in'"
        @click="openSignInModalClickHandler"
      >
        {{ sectionContent.signIn }}
      </drop-down-menu-item>

      <!-- Sign Up -->
      <drop-down-menu-item
        :type="'button'"
        :icon-class="'far fa-user-plus'"
        @click="openSignUpModalClickHandler"
      >
        {{ sectionContent.signUp }}
      </drop-down-menu-item>
    </template>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import applicationFlows from '@/constants/applicationFlows';

  import orderModeNavigationMixin from '@/mixins/order-mode-navigation';

  import DropDownMenuItem from '../drop-down-menu-item/index.vue';


  export default {
    name: 'AccountNavigation',

    components: {
      DropDownMenuItem
    },

    mixins: [orderModeNavigationMixin],

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('savedCards', ['creditCardManagementEnabled']),
      ...mapGetters('session', ['applicationFlow', 'currentUser', 'orderDetails', 'unreadInboxMessageCount']),
      ...mapGetters('giftCards', ['giftCardManagementEnabled', 'giftCardGiftingEnabled']),
      ...mapGetters('offers', ['loyalty', 'rewardCount']),
      ...mapGetters('merchant', ['loyaltyOptions', 'merchant']),

      sectionContent() {
        const content = this.content.MenuDropdown.metadata;

        return {
          title: content.titleAccount,
          orderFavorites: content.itemFavorites,
          myAccount: content.itemMyAccount,
          inbox: content.itemInbox,
          favoriteStore: content.itemFavoriteStore,
          loyaltyRewards: content.itemRewards,
          loyaltyOffers: content.itemOffers,
          paymentMethods: content.itemPaymentMethods,
          orderHistory: content.itemOrderHistory,
          signOut: content.itemSignOut,
          signIn: content.itemSignIn,
          signUp: content.itemCreateAccount
        };
      },

      isOrderAheadFlow() {
        return this.applicationFlow === applicationFlows.ORDER_AHEAD;
      },

      isUserSignedIn() {
        return this.currentUser.accountId;
      },

      isOrderFavoritesEnabled() {
        return !this.merchant.features['hide-favorites'];
      },

      orderFavoritesNavigationLink() {
        return { name: 'orderFavorites' };
      },

      accountNavigationLink() {
        return { name: 'myAccount' };
      },

      offersNavigationLink() {
        return { name: 'offers' };
      },

      paymentMethodsNavigationLink() {
        return { name: 'paymentMethods' };
      },

      loyaltyNavigationLink() {
        return { name: 'loyalty' };
      },

      orderHistoryNavigationLink() {
        return { name: 'orderHistory' };
      },

      hasEarnedRewards() {
        return this.rewardCount > 0;
      },

      isFavoriteStoreEnabled() {
        return this.merchant.userSignUpFields.favoriteStoreId;
      },

      isOrdersLoyaltyEnabled() {
        return this.loyaltyOptions.isVisible;
      },

      isLoyaltyOffersEnabled() {
        return this.merchant.options.offersOptions.offersEnabled;
      },

      isProgressRingEnabled() {
        return this.loyalty.isEnrolled && this.loyalty.finalThreshold && !this.loyalty.isMembershipTierStatus;
      },

      isProgressRingIndicatorEnabled() {
        return this.loyalty.profileShowProgressIndicatorEnabled;
      },

      isPaymentManagementEnabled() {
        return this.creditCardManagementEnabled || this.giftCardManagementEnabled;
      },

      isOrderHistoryEnabled() {
        return !this.merchant.features['hide-order-history'];
      },

      isLoyaltySignOnEnabled() {
        return this.loyaltyOptions.supportsLoyaltySso;
      },

      isMessageInboxEnabled() {
        return this.merchant.options.messageInboxEnabled;
      },

      inboxNavigationLink() {
        return { name: 'inbox' };
      }
    },

    methods: {
      favoriteStoreClickHandler() {
        const modalPayload = {
          selectedStoreId: this.currentUser.favoriteStore.storeId,
          onFavoriteStoreSelection: ({ storeId }) => this.$store.dispatch('session/updateUsersFavoriteStore', storeId)
        };

        this.$openModal('selectFavoriteStore', modalPayload);
      },

      openSignInModalClickHandler() {
        if (this.isLoyaltySignOnEnabled) {
          this.$openModal('single-sign-on-modal');
        }
        else {
          this.$openModal('signIn');
        }
      },

      openSignUpModalClickHandler() {
        this.$openModal('createAccount');
      },

      signOutClickHandler() {
        const dispatchPayload = { userInitiated: true };

        this.$store.dispatch('session/signOut', dispatchPayload);

        const routerPayload = { name: 'order', params: { merchantId: this.$route.params.merchantId } };

        this.$router.push(routerPayload);

        // this.$triggerGoogleTrackingEvent('SignOut', 'Account');

        this.$triggerNewRelicEvent('SignOut', 'Information', 'Sign Out');
      }
    }
  };
</script>
